import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { transactionService } from '../../../services/transactionService';

const CompactBillCard = ({ bill, onEdit, onDelete, onToggleStatus, onTransactionStatusChange }) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [hasTransaction, setHasTransaction] = useState(false);
  const [checkingTransaction, setCheckingTransaction] = useState(true);
  
  useEffect(() => {
    checkTransactionExists();
  }, [bill]);
  
  const checkTransactionExists = async () => {
    try {
      setCheckingTransaction(true);
      
      if (!bill?.accountId || !bill?.nextExecutionDate) {
        setHasTransaction(false);
        // Report status to parent
        if (onTransactionStatusChange) {
          onTransactionStatusChange(bill?.id, false);
        }
        return;
      }
      
      const nextDate = new Date(bill?.nextExecutionDate);
      nextDate?.setHours(0, 0, 0, 0);
      
      // Get all transactions for the bill's account
      const transactions = await transactionService?.getByAccount(bill?.accountId);
      
      if (!transactions || transactions?.length === 0) {
        setHasTransaction(false);
        // Report status to parent
        if (onTransactionStatusChange) {
          onTransactionStatusChange(bill?.id, false);
        }
        return;
      }
      
      // Format the target date as YYYY-MM-DD for same-day comparison
      const targetDateStr = nextDate?.toISOString()?.split('T')?.[0];
      
      // Get day of week (0 = Sunday, 6 = Saturday)
      const dayOfWeek = nextDate?.getDay();
      
      // Build array of dates to check
      const datesToCheck = [targetDateStr];
      
      // If Saturday (6), also check Friday before
      if (dayOfWeek === 6) {
        const fridayBefore = new Date(nextDate);
        fridayBefore?.setDate(fridayBefore?.getDate() - 1);
        datesToCheck?.push(fridayBefore?.toISOString()?.split('T')?.[0]);
      }
      
      // If Sunday (0), also check Friday before and Monday after
      if (dayOfWeek === 0) {
        const fridayBefore = new Date(nextDate);
        fridayBefore?.setDate(fridayBefore?.getDate() - 2);
        datesToCheck?.push(fridayBefore?.toISOString()?.split('T')?.[0]);
        
        const mondayAfter = new Date(nextDate);
        mondayAfter?.setDate(mondayAfter?.getDate() + 1);
        datesToCheck?.push(mondayAfter?.toISOString()?.split('T')?.[0]);
      }
      
      // PRIMARY MATCHING: Check by recurring_transaction_id (most reliable)
      const matchByRecurringId = transactions?.some(txn => {
        // Must have recurring_transaction_id that matches this bill
        if (!txn?.recurringTransactionId || txn?.recurringTransactionId !== bill?.id) return false;
        
        // Must be on one of the target dates (exact date or weekend adjustment)
        const txnDateStr = txn?.date?.split('T')?.[0];
        return datesToCheck?.includes(txnDateStr);
      });
      
      if (matchByRecurringId) {
        setHasTransaction(true);
        // Report status to parent
        if (onTransactionStatusChange) {
          onTransactionStatusChange(bill?.id, true);
        }
        return;
      }
      
      // FALLBACK MATCHING: If no recurring_transaction_id match, use comprehensive matching
      const matchByAttributes = transactions?.some(txn => {
        // Extract date from transaction
        const txnDateStr = txn?.date?.split('T')?.[0];
        
        // Must match one of the target dates (exact date or weekend adjustment)
        if (!datesToCheck?.includes(txnDateStr)) return false;
        
        // Must match account
        if (txn?.accountId !== bill?.accountId) return false;
        
        // Must match type (income/expense)
        if (txn?.type !== bill?.type) return false;
        
        // Match by amount (use ABS to avoid sign mismatch, allow 0.01 tolerance)
        const billAmount = Math.abs(parseFloat(bill?.amount) || 0);
        const txnAmount = Math.abs(parseFloat(txn?.amount) || 0);
        const amountMatch = Math.abs(billAmount - txnAmount) < 0.01;
        
        if (!amountMatch) return false;
        
        // Optional: Match by category if both have it
        if (bill?.categoryId && txn?.categoryId && bill?.categoryId !== txn?.categoryId) {
          return false;
        }
        
        // All criteria matched
        return true;
      });
      
      setHasTransaction(matchByAttributes);
      // Report status to parent
      if (onTransactionStatusChange) {
        onTransactionStatusChange(bill?.id, matchByAttributes);
      }
    } catch (err) {
      console.error('Error checking transaction:', err);
      setHasTransaction(false);
      // Report status to parent
      if (onTransactionStatusChange) {
        onTransactionStatusChange(bill?.id, false);
      }
    } finally {
      setCheckingTransaction(false);
    }
  };

  if (!bill) return null;

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      const date = new Date(dateString);
      if (isNaN(date?.getTime())) return 'Invalid date';
      return date?.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric'
      });
    } catch (err) {
      return 'Invalid date';
    }
  };

  const getFrequencyLabel = (frequency) => {
    if (!frequency) return 'Unknown';
    const labels = {
      daily: 'Daily',
      weekly: 'Weekly',
      biweekly: 'Biweekly',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly'
    };
    return labels?.[frequency] || frequency;
  };

  const getStatusColor = (status) => {
    if (status === 'active') return 'text-success';
    if (status === 'paused') return 'text-muted-foreground';
    return 'text-muted-foreground';
  };

  const getStatusBgColor = (status) => {
    if (status === 'active') return 'bg-success/10';
    if (status === 'paused') return 'bg-muted/30';
    return 'bg-muted/30';
  };

  const getDaysUntilNext = () => {
    if (!bill?.nextExecutionDate) return null;
    try {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      const nextDate = new Date(bill.nextExecutionDate);
      if (isNaN(nextDate?.getTime())) return null;
      nextDate?.setHours(0, 0, 0, 0);
      const diffTime = nextDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays < 0) return 'Overdue';
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Tomorrow';
      return `In ${diffDays} days`;
    } catch (err) {
      return null;
    }
  };

  const daysUntil = getDaysUntilNext();
  const isPastDue = (daysUntil === 'Overdue' || daysUntil === 'Today') && !hasTransaction;

  const handleCreateTransaction = () => {
    if (!bill?.accountId) return;
    
    navigate('/bill-payment-confirmation', {
      state: {
        billData: {
          description: bill?.transactionName,
          amount: bill?.amount,
          type: bill?.type,
          categoryId: bill?.categoryId,
          date: bill?.nextExecutionDate,
          frequency: bill?.frequency
        },
        accountId: bill?.accountId
      }
    });
  };

  return (
    <div className="bg-card border-b border-border p-12 hover:bg-muted/30 transition-colors relative">
      <div className="flex items-start gap-12">
        {/* Left: Icon + Info */}
        <div 
          className="w-36 h-36 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${bill?.categoryColor || '#6366f1'}20` }}
        >
          <Icon 
            name={bill?.categoryIcon || 'Tag'} 
            size={18} 
            color={bill?.categoryColor || '#6366f1'} 
          />
        </div>

        {/* Middle: Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-8 mb-4">
            <h3 className="text-sm font-semibold text-foreground truncate">
              {bill?.transactionName || 'Unnamed Bill'}
            </h3>
            <div className="flex items-center gap-8 flex-shrink-0">
              <span className="text-sm font-bold text-foreground">
                {formatCurrency(bill?.amount)}
              </span>
            </div>
          </div>

          {/* Category label */}
          {bill?.category && (
            <div className="inline-flex items-center px-6 py-1 bg-muted/50 rounded text-xs text-muted-foreground mb-6">
              {bill?.category}
            </div>
          )}

          {/* Second line: account + frequency + next due */}
          <div className="flex items-center gap-8 text-xs text-muted-foreground flex-wrap">
            {bill?.account && (
              <span className="truncate">{bill?.account}</span>
            )}
            <span>•</span>
            <span>{getFrequencyLabel(bill?.frequency)}</span>
            <span>•</span>
            <span>Next: {formatDate(bill?.nextExecutionDate)}</span>
          </div>
        </div>

        {/* Right: Status + Menu */}
        <div className="flex items-start gap-8 flex-shrink-0">
          {hasTransaction && (
            <div className="px-8 py-4 bg-success/10 rounded-lg">
              <span className="text-xs font-medium text-success">Paid</span>
            </div>
          )}
          <div className={`inline-flex items-center px-6 py-1 rounded-full text-xs font-medium ${getStatusBgColor(bill?.status)} ${getStatusColor(bill?.status)}`}>
            {bill?.status === 'active' ? 'Active' : 'Paused'}
          </div>
          
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-6 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="MoreVertical" size={16} color="var(--color-muted-foreground)" />
            </button>

            {/* Action Menu */}
            {showMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-full mt-4 bg-card border border-border rounded-lg shadow-lg z-20 min-w-[140px] overflow-hidden">
                  {isPastDue && (
                    <button
                      onClick={() => {
                        handleCreateTransaction();
                        setShowMenu(false);
                      }}
                      className="w-full px-12 py-8 text-left text-sm text-success hover:bg-success/10 transition-colors flex items-center gap-8"
                    >
                      <Icon name="Plus" size={14} color="var(--color-success)" />
                      Create Transaction
                    </button>
                  )}
                  <button
                    onClick={() => {
                      onEdit(bill);
                      setShowMenu(false);
                    }}
                    className="w-full px-12 py-8 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-8"
                  >
                    <Icon name="Edit" size={14} color="var(--color-foreground)" />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      onToggleStatus(bill);
                      setShowMenu(false);
                    }}
                    className="w-full px-12 py-8 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-8"
                  >
                    <Icon 
                      name={bill?.status === 'active' ? 'Pause' : 'Play'} 
                      size={14} 
                      color="var(--color-foreground)" 
                    />
                    {bill?.status === 'active' ? 'Pause' : 'Resume'}
                  </button>
                  <button
                    onClick={() => {
                      onDelete(bill?.id);
                      setShowMenu(false);
                    }}
                    className="w-full px-12 py-8 text-left text-sm text-error hover:bg-error/10 transition-colors flex items-center gap-8"
                  >
                    <Icon name="Trash2" size={14} color="var(--color-error)" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompactBillCard;